import FormBase from '@/components/forms/new/FormBase';
import { Box } from '@chakra-ui/react';
import ServiceProfileVisibleScope from './formFields/ServiceProfileVisibleScope';
import ServiceProfileNameField from './formFields/ServiceProfileNameField';
import PlatformNameField from './formFields/PlatformNameField';
import ServiceProfileDescShort from './formFields/ServiceProfileDescShort';
// import ServiceProfileDescLong from './formFields/ServiceProfileDescLong';
import ServiceProfileIconLargeURL from './formFields/ServiceProfileIconLargeURL';
import ServiceProfileIconSmallURL from './formFields/ServiceProfileIconSmallURL';
import PlatformVendorID from './formFields/PlatformVendorID';
import { useEffect, useState, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import updateServiceProfiles from 'API/ServiceProfiles/updateServiceProfiles';
import { toSentenceCase } from 'utils/utils';
import Alert from 'components/v4/Alert';
import AlertDescription from 'components/v4/AlertDescription';
import AlertIcon from 'components/v4/AlertIcon';
import createServiceProfile from 'API/ServiceProfiles/createServiceProfile';
import ServiceProfileLoader from 'pages/services/serviceprofiles/ServiceProfileLoader';
import getOperatorPlatformServiceProfile from 'API/ServiceProfiles/getOperatorPlatformServiceProfile';
import useFieldValidation from 'hooks/useFieldValidation';
import useFileUpload from 'hooks/useFileUpload';
import useDetectFileType from 'hooks/useDetectFileType';
import uploadOperatorPlatformLogo from 'API/ServiceProfiles/uploadOperatorPlatformLogo';

import { validationSchemaDR } from './schema';

import BreadcrumbTrail from 'components/BreadcrumbTrail';
import useGetCurrentWholesaler from '@/queries/wholesaler/useGetCurrentWholesaler';
import { resolveWholesalerProfileName } from './serviceProfileName';

const OperatorPlatformServiceProfile = ({
    mode,
    data,
    level,
    handleBackButton,
    visibleScopeOptions,
}) => {
    const {
        currentWholesaler: wholesalerID,
        currentPartner: partnerID,
        companiesAndPartners,
    } = useSelector((state) => state.navigationLists);
    const { data: currentWholesalerData } = useGetCurrentWholesaler();
    const { requiredScope } = useSelector((state) => state.login);

    const { darkMode } = useSelector((state) => state.settings);

    const DEFAULT_OPERATOR_PLATFORM_PAYLOAD = {
        name: resolveWholesalerProfileName(null, 'Operator Platform'),
        partnerID: level === 'partner' ? partnerID : undefined,
        wholesalerID: level === 'partner' ? undefined : wholesalerID,
        countriesLists: [],
        customPlatformName: '',
        customShortText: '',
        iconLargeURL: '',
        iconSmallURL: '',
        visibleScope: level === 'partner' ? 20 : 60,
    };

    const [modalState, setModalState] = useState(
        data || DEFAULT_OPERATOR_PLATFORM_PAYLOAD,
    );

    const imageBaseURL =
        import.meta.env.VITE_APP_IMAGE_BASE +
        '/images/uploads/' +
        modalState?.customPlatformLogo;
    // File handler
    const logoFileRefLarge = useRef(null);
    const {
        uploadHandler: uploadHandlerLarge,
        fileReaderResult: fileReaderResultLarge,
        file: fileLarge,
    } = useFileUpload({
        allowedExtensions: ['.png'],
        logoFileRef: logoFileRefLarge,
    });
    const logoFileTypeLarge = useDetectFileType(fileReaderResultLarge);

    // File handler for small icon
    const logoFileRefSmall = useRef(null);
    const {
        uploadHandler: uploadHandlerSmall,
        fileReaderResult: fileReaderResultSmall,
        file: fileSmall,
    } = useFileUpload({
        allowedExtensions: ['.png'],
        logoFileRef: logoFileRefSmall,
    });
    const logoFileTypeSmall = useDetectFileType(
        fileReaderResultSmall || imageBaseURL,
    );

    useEffect(() => {
        if (
            mode !== 'EDIT' &&
            level !== 'partner' &&
            currentWholesalerData?.name
        ) {
            handleChangeState({
                name: resolveWholesalerProfileName(
                    currentWholesalerData?.name,
                    'Operator Platform',
                ),
            });
        } else if (
            mode !== 'EDIT' &&
            level === 'partner' &&
            companiesAndPartners?.length > 0
        ) {
            const partnerName = companiesAndPartners.find(
                ({ id }) => id === partnerID,
            )?.name;
            handleChangeState({
                name: `${partnerName} - Operator Platform Partner Profile`,
            });
        }
    }, [
        mode,
        companiesAndPartners,
        currentWholesalerData?.name,
        level,
        wholesalerID,
        partnerID,
    ]);

    const extractErrorMessage = (e) =>
        e?.response?.data?.message ||
        e?.message ||
        'An error occurred. Please try again later.';

    const [error, setError] = useState(''); // keep as string
    const [isLoading, setIsLoading] = useState(null);

    useFieldValidation(modalState, validationSchemaDR);

    const scope = useCallback(() => {
        if (level === 'partner') {
            return `Partner/${data?.partnerID || partnerID}`;
        } else {
            return `Wholesaler/${data?.wholesalerID || wholesalerID}`;
        }
    }, [level, partnerID, wholesalerID, data?.wholesalerID, data?.partnerID]);
    const handleChangeState = (payload) => {
        setModalState((prev) => ({
            ...prev,
            ...payload,
        }));
    };

    const handleGetDropdown = useCallback(() => {
        setIsLoading(true);
        const fetchSPData = mode === 'EDIT' && data;
        Promise.all([
            fetchSPData
                ? getOperatorPlatformServiceProfile({ scope: scope() })
                : setTimeout(() => {
                      Promise.resolve();
                  }, 1),
        ])
            .then(([spResult]) => {
                if (fetchSPData) {
                    setModalState({
                        ...spResult.data,
                    });
                }
            })
            .catch((e) => {
                setError(extractErrorMessage(e));
            })
            .finally(() => {
                setTimeout(() => setIsLoading(false), 500);
            });
    }, [mode, data, scope]);

    useEffect(() => {
        handleGetDropdown();
    }, [mode, data, level, handleGetDropdown]);

    if (isLoading) {
        return <ServiceProfileLoader />;
    }

    const initialValues = {
        name: modalState?.name || '',
        partnerID:
            level === 'partner'
                ? (modalState?.partnerID ?? partnerID)
                : undefined,
        wholesalerID:
            level === 'partner'
                ? undefined
                : (modalState?.wholesalerID ?? wholesalerID),
        visibleScope:
            modalState?.visibleScope ?? (level === 'partner' ? 20 : 60),
        customPlatformName: modalState?.customPlatformName || '',
        customShortText: modalState?.customShortText || '',
        customPlatformLogo: imageBaseURL || '',
        // descriptionLong: modalState?.descriptionLong || '',
    };

    const finalFormSubmit = async (values, actions) => {
        setError('');
        setIsLoading(true);
        try {
            const payload = {
                type: 'OperatorPlatform',
                payload: modalState,
                level,
                scope: requiredScope,
            };
            if (mode === 'EDIT') {
                await updateServiceProfiles(payload).then((res) => {
                    handleChangeState(res.data);
                    let formData = new FormData();
                    if (fileLarge) {
                        formData.append('Logo', fileLarge);
                    }
                    if (fileSmall) {
                        formData.append('Logo', fileSmall);
                    }
                    level === 'wholesaler' &&
                        uploadOperatorPlatformLogo({
                            wholesalerId: wholesalerID,
                            payload: formData,
                            scope: requiredScope,
                        });
                });
            } else {
                await createServiceProfile(payload)?.then((res) => {
                    handleChangeState(res.data);
                    let formData = new FormData();
                    if (fileLarge) {
                        formData.append('Logo', fileLarge);
                    }
                    if (fileSmall) {
                        formData.append('Logo', fileSmall);
                    }

                    level === 'wholesaler' &&
                        uploadOperatorPlatformLogo({
                            wholesalerId: wholesalerID,
                            payload: formData,
                            scope: requiredScope,
                        });
                });
            }
            handleBackButton();
        } catch (e) {
            setError(extractErrorMessage(e));
        } finally {
            setIsLoading(false);
            actions.setSubmitting(false);
        }
    };
    const breadCrumbTrail = [
        { label: 'Services', path: ['', { index: 0 }] },
        { label: 'Configurations', path: '' },
    ];

    // TODO: Enable large icon upload when needed
    const largeIconEnabled = false;
    return (
        <>
            <BreadcrumbTrail
                breadCrumbTrail={breadCrumbTrail}
                handleBackButton={handleBackButton}
            />
            <FormBase
                header={`${toSentenceCase(mode)} ${modalState.name || data?.name}`}
                subTitleHeader={true}
                backLink={['/service-profiles', { state: { index: 0 } }]}
                ufBackBtn="operatorPlatformServiceProfileBackBtn"
                ufSaveBtn="operatorPlatformServiceProfileSaveBtn"
                initialValues={initialValues}
                finalFormSubmit={finalFormSubmit}
                customClick={true}
                onBack={handleBackButton}
                isLoading={isLoading}
                enableReinitialize={true}>
                {({ errors = {}, touched = {} }) =>
                    [
                        <ServiceProfileNameField
                            key="name"
                            errors={errors}
                            touched={touched}
                            onChange={(e) => {
                                setModalState((prev) => ({
                                    ...prev,
                                    name: e.target.value,
                                }));
                            }}
                            darkMode={darkMode}
                        />,
                        <ServiceProfileVisibleScope
                            key="operatorPlatformServiceProfileVisibleScope"
                            options={visibleScopeOptions}
                            darkMode={darkMode}
                            onChange={(e) => {
                                setModalState((prev) => ({
                                    ...prev,
                                    visibleScope: e.value,
                                }));
                            }}
                        />,
                        level === 'wholesaler' && (
                            <PlatformNameField
                                key="customPlatformName"
                                errors={errors}
                                touched={touched}
                                onChange={(e) => {
                                    setModalState((prev) => ({
                                        ...prev,
                                        customPlatformName: e.target.value,
                                    }));
                                }}
                                darkMode={darkMode}
                            />
                        ),

                        level === 'wholesaler' && (
                            <ServiceProfileDescShort
                                key="customShortText"
                                errors={errors}
                                touched={touched}
                                darkMode={darkMode}
                                onChange={(e) => {
                                    setModalState((prev) => ({
                                        ...prev,
                                        customShortText: e.target.value,
                                    }));
                                }}
                            />
                        ),
                        // level === 'wholesaler' && (
                        //     <ServiceProfileDescLong
                        //         key="descriptionLong"
                        //         errors={errors}
                        //         touched={touched}
                        //         darkMode={darkMode}
                        //     />),

                        level === 'wholesaler' && largeIconEnabled && (
                            <ServiceProfileIconLargeURL
                                key="iconLargeURL"
                                errors={errors}
                                touched={touched}
                                darkMode={darkMode}
                                uploadHandler={uploadHandlerLarge}
                                fileReaderResult={fileReaderResultLarge}
                                logoFileType={logoFileTypeLarge}
                                logoFileRef={logoFileRefLarge}
                            />
                        ),
                        level === 'wholesaler' && (
                            <ServiceProfileIconSmallURL
                                key="iconSmallURL"
                                errors={errors}
                                touched={touched}
                                darkMode={darkMode}
                                uploadHandler={uploadHandlerSmall}
                                fileReaderResult={
                                    fileReaderResultSmall || imageBaseURL
                                }
                                logoFileType={logoFileTypeSmall}
                                logoFileRef={logoFileRefSmall}
                            />
                        ),
                        mode === 'EDIT' && level === 'wholesaler' && (
                            <PlatformVendorID
                                key="vendorID"
                                errors={errors}
                                touched={touched}
                                darkMode={darkMode}
                            />
                        ),
                        error && (
                            <Box
                                paddingBottom="0.5rem"
                                key="error-box-operator-platform">
                                <Alert
                                    key="error"
                                    status="error"
                                    mt={4}
                                    marginBottom="1rem">
                                    <AlertIcon />
                                    <AlertDescription>{error}</AlertDescription>
                                </Alert>
                            </Box>
                        ),
                    ].filter(Boolean)
                }
            </FormBase>
        </>
    );
};

export default OperatorPlatformServiceProfile;
